// src/core/composables/useUserProfileApi.ts
import { ref } from "vue";
import type { UserContext } from "@/core/store/context";
import type { DataPermissions, PagePermissions, UserPermissions } from "@/models";
import type { UserRole } from "@/core/store/context/UserContext";
import { RoleEnum } from "@/core/enum";
import { userProfileService } from "@/core/services/userProfile.service";
import { createApiComposable } from "@/core/composables/createApiComposable";

interface Permission {
  key: string;
  value: boolean;
}

function flattenPermissions(perms: UserPermissions) {
  const dataObj = (perms?.data ?? {}) as Partial<DataPermissions>;
  const pageObj = (perms?.page ?? {}) as Partial<PagePermissions>;

  const data: Permission[] = Object.keys(dataObj).map((key) => ({
    key,
    value: Boolean(dataObj[key as keyof DataPermissions]),
  }));

  const page: Permission[] = Object.keys(pageObj).map((key) => ({
    key,
    value: Boolean(pageObj[key as keyof PagePermissions]),
  }));

  return { data, page };
}

export function useUserProfileApi() {
  const userContext = ref<UserContext>({} as UserContext);
  const user = ref<UserContext>({} as UserContext);

  const dataPermissions = ref<Permission[]>([]);
  const pagePermissions = ref<Permission[]>([]);

  const permissions = ref<UserPermissions>({} as UserPermissions);
  const role = ref<UserRole>(RoleEnum.EMPLOYEE);

  const authenticated = ref(false);

  const api = createApiComposable({
    defaultErrorMessage: "Failed to load profile",
  });

  const applyUserContext = (ctx: UserContext) => {
    ctx.permissions ??= {} as any;
    (ctx.permissions as any).data ??= {};
    (ctx.permissions as any).page ??= {};

    userContext.value = ctx;
    user.value = ctx;

    permissions.value = ctx.permissions;
    role.value = ctx.role;

    const flattened = flattenPermissions(ctx.permissions);
    dataPermissions.value = flattened.data;
    pagePermissions.value = flattened.page;
  };

  const fetchUserProfileById = async (id: string) => {
    const ctx = await api.run(
      () => userProfileService.fetchUserProfileById(id),
      "Failed to load profile"
    );
    applyUserContext(ctx);
    return ctx;
  };

  const fetchUserProfile = async () => {
    try {
      const ctx = await api.run(
        () => userProfileService.fetchUserProfile(),
        "Failed to load profile"
      );
      applyUserContext(ctx);
      authenticated.value = true;
      return ctx;
    } catch (e) {
      authenticated.value = false;
      throw e;
    }
  };

  return {
    userContext,
    user,
    dataPermissions,
    pagePermissions,
    authenticated,
    role,

    // expose shared api state
    loading: api.loading,
    error: api.error,
    clearError: api.clearError,

    fetchUserProfile,
    fetchUserProfileById,
  };
}
